<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const atualizacoes = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const sortField = ref('')
const sortOrder = ref('')

const temPermissaoCompleta = computed(() => authStore.pode('ATUALIZACAO_CUSTOS'))

const colunas = [
  { field: 'idAtualizacaoCusto', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'lstStatus', label: 'Status', width: 120, align: 'center', slotName: 'status' },
  { field: 'tsAtualizacao', label: 'Data/Hora', sortable: true, slotName: 'tsAtualizacao' },
  { field: 'motivo', label: 'Motivo', sortable: true, slotName: 'motivo' },
  { field: 'qtdProdutosAfetados', label: 'Qtd. Produtos', width: 130, align: 'center', slotName: 'qtd' },
  { field: 'nomeUsuarioCriacao', label: 'Aplicado Por', width: 180, slotName: 'usuario' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, _order, _data) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    atualizacoes.value = []
    paginacao.value.TotalCount = 0
  }
})

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    atualizacoes.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    // O escopo por organização é resolvido no backend via header 'organizacaoAtiva' (interceptor).
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/AtualizacoesCusto?${params.toString()}`)
    atualizacoes.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar o histórico de atualizações de custos.'
    atualizacoes.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const abrirAuditoria = (row) => {
  auditoriaSelecionada.id = row.idAtualizacaoCusto
  auditoriaSelecionada.nome = `Atualização #${row.idAtualizacaoCusto}`
  mostrarAuditoria.value = true
}

const editarItem = (row) => {
  emit('editar-item', row)
}

onMounted(() => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Atualização de Custos"
    subtitulo="Histórico de atualizações de custos em lote. Operação imutável (append-only)."
    :filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">{{ erro }}</div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="atualizacoes"
      :loading="loading"
      empty-message="Nenhuma atualização de custo registrada."
      row-key="idAtualizacaoCusto"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idAtualizacaoCusto }}</span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="row.lstStatus === 'CUSTO_ST2' ? 'badge-ghost-success' : 'badge-ghost-warning'">
          {{ row.lstStatus === 'CUSTO_ST2' ? 'Aplicada' : 'Rascunho' }}
        </span>
      </template>

      <template #tsAtualizacao="{ row }">
        <span class="fw-bold">
          <i class="bi bi-clock-history me-2 text-primary"></i>
          {{ row.tsAtualizacao ? formatarDataHoraBR(row.tsAtualizacao) : '—' }}
        </span>
      </template>

      <template #motivo="{ row }">
        <span class="text-body">{{ row.motivo }}</span>
        <div v-if="row.observacao" class="text-body-secondary small fst-italic">{{ row.observacao }}</div>
      </template>

      <template #qtd="{ row }">
        <span class="badge badge-ghost-primary">{{ row.qtdProdutosAfetados }}</span>
      </template>

      <template #usuario="{ row }">
        <span class="text-body-secondary small">
          <i class="bi bi-person-fill me-1"></i>
          {{ row.nomeUsuarioCriacao || '—' }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico de Alterações">
            <i class="bi bi-clock-history"></i>
          </button>
          <button
            class="btn-icon"
            @click="editarItem(row)"
            :title="row.lstStatus === 'CUSTO_ST2' ? 'Visualizar' : 'Editar Rascunho'"
          >
            <i :class="row.lstStatus === 'CUSTO_ST2' ? 'bi bi-eye-fill text-secondary' : 'bi bi-pencil-fill text-primary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="AtualizacaoCusto"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
